import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { containerPermissions } from "@/lib/boo-permissions";
import { rememberVaultOpened, showsVaultGate, vaultOpened } from "@/lib/vault-gate";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { KeyRound, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { VaultTab } from "./VaultTab";

// The vault tab opens on a warning first. VaultTab (and its secrets fetch)
// mounts only after "Open the vault", which is then remembered per Boo in
// this browser.
export function VaultGate() {
  const params = useParams({ strict: false }) as { containerId?: string };
  const containerId = params.containerId ?? "";
  // Keyed so moving to another Boo's vault re-reads that Boo's choice.
  return <Gate key={containerId} containerId={containerId} />;
}

function Gate({ containerId }: { containerId: string }) {
  const [open, setOpen] = useState(() => vaultOpened(containerId));
  const [justOpened, setJustOpened] = useState(false);
  const vaultRef = useRef<HTMLDivElement>(null);

  // The button that had focus is gone; move focus to the vault page heading.
  useEffect(() => {
    if (!justOpened) return;
    const heading = vaultRef.current?.querySelector<HTMLElement>("h1");
    if (!heading) return;
    heading.tabIndex = -1;
    heading.classList.add("focus:outline-none");
    heading.focus();
  }, [justOpened]);
  // The Boo's layout shows its own error page when this list fails.
  const { data: listResponse } = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });

  if (!listResponse) return null;
  const container = listResponse.data.find((c) => c.id === containerId);
  if (!container) return null;
  const gate = showsVaultGate({
    opened: open,
    isBrain: container.agentType === "boo-brain",
    canManage: containerPermissions(container, listResponse._permissions)?.canManageVault === true,
  });
  if (!gate) {
    return (
      <div ref={vaultRef} className="contents">
        <VaultTab />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto p-6 gap-4">
      <PageHeader title="Vault" />
      <div className="mx-auto mt-6 flex w-full max-w-md flex-col items-center gap-4 text-center">
        <span className="flex size-10 items-center justify-center rounded-full bg-muted">
          <KeyRound className="size-5 text-muted-foreground" />
        </span>
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold">This Boo&apos;s vault</h2>
          <p className="text-sm text-muted-foreground text-pretty">
            The vault holds the passwords, keys and tokens Boo uses. It&apos;s meant for IT and technical admins.
          </p>
        </div>
        <div
          role="note"
          className="flex w-full items-start gap-2 rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-left text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <p className="text-pretty">
            If something stored here gets broken or misconfigured, Boo Lab can&apos;t help recover it. Only change what
            you understand.
          </p>
        </div>
        <Button
          onClick={() => {
            rememberVaultOpened(containerId);
            setOpen(true);
            setJustOpened(true);
          }}
        >
          Open the vault
        </Button>
      </div>
    </div>
  );
}
